import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Building2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { PageHead } from "../components/Bits";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";

const today = () => new Date().toISOString().slice(0, 10);
const DEMO = {
  customer: {
    name: "Ananya Sharma",
    phone: "+91 98450 00000",
    email: "customer.demo@possessioncheck.com",
  },
  property: {
    property_type: "Apartment",
    project: "Prestige Sample Residency",
    builder: "[Builder name]",
    location: "Sarjapur Road, Bangalore",
    tower: "C",
    flat: "C-1204",
    floor: "12",
    total_floors: "22",
    bhk: "3",
    bathroom_count: "3",
    super_area: "1850",
    carpet_area: "1320",
    age: "New",
    status: "New",
    possession_status: "Pre-possession",
  },
};

const Pick = ({ label, value, onChange, options, testid }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          type="button"
          key={o}
          onClick={() => onChange(o)}
          data-testid={`${testid}-${String(o).toLowerCase().replace(/\s+/g, "-")}`}
          className={`tap h-11 rounded-md border px-3.5 text-sm font-semibold ${String(value) === String(o) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

export default function PropertyForm() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isAdmin = user.role === "admin";
  const [c, setC] = useState({ name: "", phone: "", email: "" });
  const [p, setP] = useState({
    property_type: "Apartment",
    bhk: "3",
    bathroom_count: "2",
    status: "New",
    possession_status: "Pre-possession",
    age: "New",
  });
  const [meta, setMeta] = useState({ service_types: [] });
  const [service, setService] = useState("Individual Inspection");
  const [date, setDate] = useState(today());
  const [inspectors, setInspectors] = useState([]);
  const [inspector, setInspector] = useState("");
  const [thermal, setThermal] = useState(false);
  const [tpl, setTpl] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data));
    if (isAdmin)
      api
        .get("/users", { params: { role: "inspector" } })
        .then(({ data }) => setInspectors(data));
  }, [isAdmin]);
  useEffect(() => {
    api
      .get("/template", {
        params: {
          property_type: p.property_type,
          bhk: p.bhk,
          bathroom_count: p.bathroom_count,
        },
      })
      .then(({ data }) => setTpl(data));
  }, [p.property_type, p.bhk, p.bathroom_count]);

  const fill = () => {
    setC(DEMO.customer);
    setP(DEMO.property);
    setThermal(true);
  };
  const sp = (k) => (v) =>
    setP((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const sc = (k) => (e) => setC((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const equipment = [
        "Tapping hammer",
        "Socket tester",
        "Spirit level",
        ...(thermal ? ["Thermal Camera (Bosch GTC 12V-450-13)"] : []),
      ];
      const property = {
        ...p,
        bhk: p.bhk,
        bathroom_count: Number(p.bathroom_count),
      };
      const { data } = await api.post("/inspections", {
        customer: c,
        property,
        inspection_date: date,
        service_type: service,
        inspector_id: inspector || null,
        equipment,
      });
      toast.success(`Inspection ${data.inspection_id} created`);
      nav(
        isAdmin
          ? "/admin/inspections"
          : `/inspector/inspection/${data.inspection_id}`,
      );
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const F = ({ k, label, obj = "p", type = "text", req }) => (
    <div>
      <Label className="text-xs" htmlFor={k}>
        {label}
      </Label>
      <Input
        id={k}
        type={type}
        required={req}
        className="mt-1.5 h-11"
        value={(obj === "p" ? p : c)[k] || ""}
        onChange={obj === "p" ? sp(k) : sc(k)}
        data-testid={`property-${k}-input`}
      />
    </div>
  );
  const items = tpl.reduce((s, r) => s + r.items, 0);
  return (
    <form
      onSubmit={submit}
      className="mx-auto max-w-5xl pb-24"
      data-testid="property-form"
    >
      <PageHead
        over={
          loc.pathname.includes("admin")
            ? "Admin · New inspection"
            : "Step 1 · Property details"
        }
        title="Create property & inspection"
      >
        <button
          type="button"
          onClick={fill}
          className="tap h-10 rounded-md border border-teal-600 px-4 text-sm font-semibold text-teal-700 hover:bg-teal-50"
          data-testid="property-fill-demo-button"
        >
          Fill sample property
        </button>
      </PageHead>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="overline mb-4">Customer</div>
            <div className="grid gap-4 sm:grid-cols-3">
              {F({ k: "name", label: "Customer name", obj: "c", req: true })}
              {F({ k: "phone", label: "Phone", obj: "c" })}
              {F({
                k: "email",
                label: "Email",
                obj: "c",
                type: "email",
                req: true,
              })}
            </div>
          </section>
          <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
            <div className="overline">Property</div>
            <Pick
              label="Property type"
              value={p.property_type}
              onChange={sp("property_type")}
              options={["Apartment", "Villa", "Independent House"]}
              testid="property-type"
            />
            <Pick
              label="BHK"
              value={p.bhk}
              onChange={sp("bhk")}
              options={["1", "2", "2.5", "3", "3.5", "4", "5"]}
              testid="property-bhk"
            />
            <Pick
              label="Bathrooms"
              value={p.bathroom_count}
              onChange={sp("bathroom_count")}
              options={["1", "2", "3", "4", "5"]}
              testid="property-bathrooms"
            />
            <div className="grid gap-4 sm:grid-cols-3">
              {F({ k: "project", label: "Project name", req: true })}
              {F({ k: "builder", label: "Builder name" })}
              {F({ k: "location", label: "Location" })}
              {F({ k: "tower", label: "Tower / Block" })}
              {F({ k: "flat", label: "Flat number", req: true })}
              {F({ k: "floor", label: "Floor" })}
              {F({ k: "total_floors", label: "Total floors" })}
              {F({ k: "super_area", label: "Super built-up (sq.ft.)" })}
              {F({ k: "carpet_area", label: "Carpet area (sq.ft.)" })}
              {F({ k: "age", label: "Property age" })}
            </div>
            <Pick
              label="Property status"
              value={p.status}
              onChange={sp("status")}
              options={["New", "Resale"]}
              testid="property-status"
            />
            <Pick
              label="Possession status"
              value={p.possession_status}
              onChange={sp("possession_status")}
              options={["Pre-possession", "Possession taken", "Occupied"]}
              testid="property-possession"
            />
          </section>
          <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
            <div className="overline">Inspection</div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Inspection date</Label>
                <Input
                  type="date"
                  className="mt-1.5 h-11"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  data-testid="property-date-input"
                />
              </div>
              <div>
                <Label className="text-xs">Service</Label>
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger
                    className="mt-1.5 h-11"
                    data-testid="property-service-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {meta.service_types.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isAdmin && (
                <div>
                  <Label className="text-xs">Assign inspector</Label>
                  <Select value={inspector} onValueChange={setInspector}>
                    <SelectTrigger
                      className="mt-1.5 h-11"
                      data-testid="property-inspector-select"
                    >
                      <SelectValue placeholder="Select inspector" />
                    </SelectTrigger>
                    <SelectContent>
                      {inspectors.map((i) => (
                        <SelectItem key={i.user_id} value={i.user_id}>
                          {i.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!isAdmin && (
                <div>
                  <Label className="text-xs">Inspector</Label>
                  <Input disabled value={user.name} className="mt-1.5 h-11" />
                </div>
              )}
            </div>
            <label className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={thermal}
                onChange={(e) => setThermal(e.target.checked)}
                className="h-5 w-5 accent-teal-600"
                data-testid="property-thermal-checkbox"
              />{" "}
              Thermal camera on site (Bosch GTC 12V-450-13)
            </label>
            <p className="font-mono text-[11px] text-slate-500">
              Report ID is generated automatically · PC-BLR-YYMMDD-NNN
            </p>
          </section>
        </div>
        <aside
          className="h-fit rounded-xl border border-slate-200 bg-white p-5 lg:sticky lg:top-20"
          data-testid="template-preview"
        >
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-teal-600" />
            <span className="overline">Auto template</span>
          </div>
          <div className="mt-2 font-display text-xl font-bold">
            {p.bhk} BHK {p.property_type}
          </div>
          <div className="font-mono text-xs text-slate-500">
            {tpl.length} areas · {items} checkpoints
          </div>
          <ol className="mt-4 max-h-80 space-y-1 overflow-auto text-sm">
            {tpl.map((r, i) => (
              <li key={r.name} className="flex justify-between">
                <span>
                  <span className="font-mono text-[10px] text-slate-400">
                    {String(i + 1).padStart(2, "0")}
                  </span>{" "}
                  {r.name}
                </span>
                <span className="font-mono text-xs text-slate-400">
                  {r.items}
                </span>
              </li>
            ))}
          </ol>
        </aside>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/90 p-3 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl justify-end">
          <button
            disabled={busy}
            className="tap flex h-12 w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-8 font-semibold text-white hover:bg-teal-700 sm:w-auto"
            data-testid="property-submit-button"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{" "}
            {isAdmin ? "Create & assign" : "Create & start inspection"}
          </button>
        </div>
      </div>
    </form>
  );
}
